import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash-es/get';
import classnames from 'classnames';
import { getImageUrl, SOURCES } from 'client/utils/image-helpers';
import { stripHttp } from 'client/utils/string-utils';
import { DEFAULT_PRICE } from 'client/site-modules/shared/components/deal/deal-constants';
import { getTransparentPricing } from 'site-modules/shared/components/profile/price-drop-alerts/alerts-utils';
import { getNoVehicleImageToDisplay } from 'site-modules/shared/utils/get-no-vehicle-image-to-display';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { Checkbox } from 'site-modules/shared/components/checkbox/checkbox';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { COMPARE_VEHICLE_TRAY_ID } from 'site-modules/shared/components/compare-vehicle-tray/compare-vehicle-tray';

import './card-compare-select.scss';

export function CardCompareSelect({
  tabIndex = 0,
  vinData,
  labelText = 'Compare',
  containerMarginClassName = '',
  customLabelClassName = '',
  isBottomSkipLink = false,
  compareSelect,
  photoConfig = null,
  isSubmittedLead = false,
  labelId = null,
  ariaLabel = null,
}) {
  function handleOnChange(event) {
    const { vehicleInfo, vin } = vinData;
    const { styleId, make, model, year, bodyType } = vehicleInfo.styleInfo;
    const { transparentPrice } = getTransparentPricing(vinData, isSubmittedLead);
    const price = getPriceString(transparentPrice, DEFAULT_PRICE);
    let altText = `${year} ${make} ${model}`;
    let imageUrl = stripHttp(get(vehicleInfo, 'photo.defaultPhoto.large.url', ''));
    if (photoConfig) {
      const imageId = get(photoConfig, 'imageId', '');
      const imageSource = get(photoConfig, 'imageSource', SOURCES.MEDIA);
      altText = get(photoConfig, 'altText', '');
      imageUrl = getImageUrl(imageId, undefined, imageSource);
    }
    const image = {
      url: imageUrl || getNoVehicleImageToDisplay(bodyType),
      alt: altText,
    };

    compareSelect.onSelect(event, { id: vin, vin, image, styleId, price, bodyType });
  }

  const { selectedCars } = compareSelect;
  const isChecked = !!selectedCars.find(car => car.vin === vinData.vin);
  const COMPARE_CHECKBOX_NAME = `compare-${vinData.vin}`;

  return (
    <div className="compare-select">
      <Checkbox
        tabIndex={tabIndex}
        id={COMPARE_CHECKBOX_NAME}
        labelText={labelText}
        containerClassName={classnames(
          'compare-checkbox d-inline-block text-gray-darker size-14 px-0_75 py-0_5',
          containerMarginClassName
        )}
        labelClassName="compare-label"
        customLabelClassName={classnames('rounded', customLabelClassName)}
        textLabelClassName="ps-1_5"
        onChange={handleOnChange}
        checked={isChecked}
        autoComplete="off"
        data-hidedelightedoninteraction="true"
        data-tracking-id="compare_cars_select"
        data-tracking-value={labelText}
        aria-label={ariaLabel}
        aria-labelledby={classnames(`checkbox-${COMPARE_CHECKBOX_NAME}`, labelId)}
      >
        {!!selectedCars.length && (
          <ScrollLink
            className={classnames('skip-link p-0 ms-0_5 btn btn-link text-transform-none visually-hidden-focusable', {
              bottom: isBottomSkipLink,
            })}
            to={COMPARE_VEHICLE_TRAY_ID}
            tabIndex={tabIndex}
            noScroll
          >
            Skip to compare selected
          </ScrollLink>
        )}
      </Checkbox>
    </div>
  );
}

CardCompareSelect.propTypes = {
  tabIndex: PropTypes.number,
  vinData: PropTypes.shape({}).isRequired,
  compareSelect: PropTypes.shape({
    onSelect: PropTypes.func.isRequired,
    selectedCars: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        image: PropTypes.shape({
          url: PropTypes.string,
          altText: PropTypes.string,
          description: PropTypes.string,
        }),
      })
    ).isRequired,
  }).isRequired,
  photoConfig: PropTypes.shape({}),
  labelText: PropTypes.string,
  containerMarginClassName: PropTypes.string,
  customLabelClassName: PropTypes.string,
  isSubmittedLead: PropTypes.bool,
  isBottomSkipLink: PropTypes.bool,
  labelId: PropTypes.string,
  ariaLabel: PropTypes.string,
};
